import React, { useState, useEffect } from 'react';
import { Html5QrcodeScanner } from 'html5-qrcode';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { equipmentLifecycleService } from '../../../lib/equipmentLifecycleService';

const EquipmentScanner = ({ onEquipmentScanned }) => {
  const [scanning, setScanning] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [scannedEquipment, setScannedEquipment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let scanner = null;

    if (scanning) {
      scanner = new Html5QrcodeScanner(
        'equipment-qr-reader',
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0
        },
        false
      );

      scanner?.render(
        async (decodedText) => {
          setScanning(false);
          scanner?.clear();
          await lookupEquipment(decodedText);
        },
        (errorMessage) => {
          // Ignore scan errors during scanning
        }
      );
    }

    return () => {
      if (scanner) {
        scanner?.clear()?.catch(() => {});
      }
    };
  }, [scanning]);

  const lookupEquipment = async (code) => {
    setLoading(true);
    setError(null);
    try {
      // Search by barcode or serial number
      const { data, error } = await equipmentLifecycleService?.getEquipmentById(code);
      
      if (error || !data) {
        setError('Equipment not found. Please check the code and try again.');
        setScannedEquipment(null);
      } else {
        setScannedEquipment(data);
        onEquipmentScanned?.(data);
      }
    } catch (err) {
      setError('Failed to lookup equipment. Please try again.');
      setScannedEquipment(null);
    } finally {
      setLoading(false);
    }
  };

  const handleManualLookup = async (e) => {
    e?.preventDefault();
    if (manualCode?.trim()) {
      await lookupEquipment(manualCode?.trim());
    }
  };

  const startScanning = () => {
    setError(null);
    setScannedEquipment(null);
    setScanning(true);
  };

  const stopScanning = () => {
    setScanning(false);
  };

  return (
    <div className="space-y-6">
      {/* Manual Entry */}
      <div className="bg-card rounded-xl border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Equipment Lookup</h3>
        <form onSubmit={handleManualLookup} className="space-y-4">
          <Input
            type="text"
            placeholder="Enter barcode or serial number"
            value={manualCode}
            onChange={(e) => setManualCode(e?.target?.value)}
          />
          <Button
            type="submit"
            variant="primary"
            iconName="Search"
            iconPosition="left"
            disabled={loading || !manualCode?.trim()}
            className="w-full"
          >
            {loading ? 'Searching...' : 'Lookup Equipment'}
          </Button>
        </form>
      </div>

      {/* QR Code Scanner */}
      <div className="bg-card rounded-xl border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">QR Code Scanner</h3>
        
        {!scanning && !scannedEquipment && (
          <div className="space-y-4">
            <div className="flex justify-center">
              <div className="w-64 h-64 bg-muted/30 rounded-2xl flex items-center justify-center">
                <Icon name="ScanLine" size={80} className="text-muted-foreground" />
              </div>
            </div>
            <Button
              onClick={startScanning}
              variant="primary"
              size="lg"
              iconName="Camera"
              iconPosition="left"
              className="w-full"
            >
              Start QR Scanner
            </Button>
            <div className="p-4 bg-warning/5 rounded-lg border border-warning/20">
              <p className="text-sm text-muted-foreground flex items-center justify-center gap-2">
                <Icon name="AlertCircle" size={16} className="text-warning" />
                Camera permission required for scanning
              </p>
            </div>
          </div>
        )}

        {scanning && (
          <div className="space-y-4">
            <div id="equipment-qr-reader" className="mx-auto"></div>
            <Button
              onClick={stopScanning}
              variant="outline"
              iconName="X"
              iconPosition="left"
              className="w-full"
            >
              Cancel Scanning
            </Button>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-error/10 border border-error rounded-lg p-4">
          <div className="flex items-center gap-2">
            <Icon name="AlertCircle" size={20} className="text-error" />
            <p className="text-sm text-error">{error}</p>
          </div>
        </div>
      )}

      {/* Scanned Equipment Display */}
      {scannedEquipment && (
        <div className="bg-success/10 border border-success rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Icon name="CheckCircle2" size={24} className="text-success" />
            <h3 className="text-lg font-semibold text-foreground">Equipment Found</h3>
          </div>
          
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Name</p>
                <p className="text-sm font-medium text-foreground">{scannedEquipment?.name}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Serial Number</p>
                <p className="text-sm font-medium text-foreground">{scannedEquipment?.serial_number}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Location</p>
                <p className="text-sm font-medium text-foreground">{scannedEquipment?.location}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Status</p>
                <p className="text-sm font-medium text-foreground capitalize">{scannedEquipment?.status}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Condition</p>
                <p className="text-sm font-medium text-foreground capitalize">{scannedEquipment?.condition}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Manufacturer</p>
                <p className="text-sm font-medium text-foreground">{scannedEquipment?.manufacturer}</p>
              </div>
            </div>

            {scannedEquipment?.next_maintenance_date && (
              <div className="pt-3 border-t border-border">
                <p className="text-xs text-muted-foreground mb-1">Next Maintenance</p>
                <p className="text-sm font-medium text-foreground">
                  {new Date(scannedEquipment?.next_maintenance_date)?.toLocaleDateString()}
                </p>
              </div>
            )}

            <div className="flex items-center gap-2 pt-3">
              <Button
                variant="outline"
                size="sm"
                iconName="ScanLine"
                onClick={() => {
                  setScannedEquipment(null);
                  setManualCode('');
                }}
                className="flex-1"
              >
                Scan Another
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EquipmentScanner;